import { useMemo, useRef, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import "./styles.css";

type TinyMceEditorProps = {
  label: string;
  value: string;
  onChange: (html: string, plainText: string) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  onImageUpload?: (files: FileList) => Promise<Array<{ url: string }>>;
  maxChars?: number;
  toolbar?: string;
  plugins?: string;
  height?: number;
};

const normalizeHtml = (html: string) =>
  html
    .replace(/\sdata-mce-[a-z-]+="[^"]*"/gi, "")
    .replace(/\sdata-mce-[a-z-]+='[^']*'/gi, "")
    .replace(/\sclass="mceSelected"/gi, "")
    .replace(/\sclass='mceSelected'/gi, "")
    .trim();

const defaultToolbar =
  "undo redo | blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | image media table";

const toPlainText = (html: string) =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export const TinyMceEditor = ({
  label,
  value,
  onChange,
  required,
  error,
  helperText,
  placeholder = "Start typing...",
  onImageUpload,
  maxChars,
  toolbar,
  plugins,
  height,
}: TinyMceEditorProps) => {
  const apiKey = process.env.REACT_APP_TINYMCE_API_KEY;
  const lastValueRef = useRef(value);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    lastValueRef.current = normalizeHtml(value || "");
    setCharCount(toPlainText(value || "").length);
  }, [value]);

  const initConfig = useMemo(
    () => ({
      menubar: false,
      toolbar: toolbar || defaultToolbar,
      plugins: plugins || "advlist lists image media table",
      branding: false,
      promotion: false,
      height: height ?? 420,
      placeholder,
      images_upload_handler: async (blobInfo: any, _progress: any) => {
        if (!onImageUpload) {
          return blobInfo?.blobUri() ?? "";
        }
        const file = blobInfo.blob();
        const dt = new DataTransfer();
        dt.items.add(file);
        const uploaded = await onImageUpload(dt.files);
        const url = uploaded?.[0]?.url;
        if (!url) {
          throw new Error("Image upload failed");
        }
        return url;
      },
      file_picker_types: "image media",
      content_style: `
        body { font-family: Inter, Arial, sans-serif; color: #1E1E1E; }
        a { color: #1976d2; }
        code { background: #f4f4f4; padding: 2px 4px; border-radius: 4px; }
      `,
    }),
    [height, onImageUpload, placeholder, plugins, toolbar]
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          {label}
        </Typography>
        {required && (
          <Typography variant="subtitle1" color="error.main">
            *
          </Typography>
        )}
      </Box>
      <Editor
        apiKey={apiKey}
        licenseKey="gpl"
        value={value}
        init={initConfig}
        onEditorChange={(html, editor) => {
          const nextHtml = normalizeHtml(html || "");
          if (nextHtml === lastValueRef.current) {
            return;
          }
          lastValueRef.current = nextHtml;
          const text = editor.getContent({ format: "text" }) || "";
          setCharCount(text.length);
          onChange(nextHtml, text);
        }}
      />
      <Box
        sx={{
          mt: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          variant="caption"
          color={error ? "error.main" : "text.secondary"}
          sx={{ minWidth: 0 }}
        >
          {helperText || " "}
        </Typography>
        {typeof maxChars === "number" && (
          <Typography
            variant="caption"
            sx={{
              color:
                charCount >= maxChars
                  ? "error.main"
                  : charCount >= maxChars * 0.9
                    ? "warning.main"
                    : "text.secondary",
              minWidth: 70,
              textAlign: "right",
              fontWeight: 500,
            }}
          >
            {charCount}/{maxChars}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TinyMceEditor;
